// ═══════════════════════════════════════════════════════════════════════════════
// ASSISTANT FULL PAGE - Interface style Claude/ChatGPT
// Layout: Sidebar (projets) | Chat | Artifacts (résultats)
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Plus, MessageSquare, FolderOpen, Clock, Trash2,
  Send, Mic, MicOff, Volume2, VolumeX, Loader2, Settings,
  PanelRightOpen, PanelRightClose, MoreHorizontal, Edit3,
  FileText, ListTodo, Receipt, Calendar, Users, Package,
  ChevronDown, ChevronRight, Sparkles, X, Search, Home, Pencil, Check,
  Copy, Download, Mail, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import ArtifactDevisInteractif from '../../components/artifacts/ArtifactDevisInteractif';
import ArtifactTasksInteractif from '../../components/artifacts/ArtifactTasksInteractif';
import ArtifactProjetInteractif, { DEMO_PROJECT } from '../../components/artifacts/ArtifactProjetInteractif';
import ArtifactSearchArtisans from '../../components/artifacts/ArtifactSearchArtisans';
import ArtifactSearchProduits from '../../components/artifacts/ArtifactSearchProduits';
import ArtifactSearchPosts from '../../components/artifacts/ArtifactSearchPosts';
import VoiceNotesSuperpouvoir from '../../components/ai/VoiceNotesSuperpouvoir';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER - Récupérer l'userId
// ═══════════════════════════════════════════════════════════════════════════════
const getUserId = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const buyerUser = localStorage.getItem('buyer_user');
  const clientUser = localStorage.getItem('client_user');
  
  if (ecosystemUser) {
    try { return JSON.parse(ecosystemUser).id; } catch(e) {}
  }
  if (buyerUser) {
    try { return JSON.parse(buyerUser).id; } catch(e) {}
  }
  if (clientUser) {
    try { return JSON.parse(clientUser).id; } catch(e) {}
  }
  return localStorage.getItem('userId');
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER - Nom personnalisé de l'assistant
// ═══════════════════════════════════════════════════════════════════════════════
const getAssistantName = () => {
  return localStorage.getItem('assistant_custom_name') || 'Mon Assistant';
};

const setAssistantName = (name) => {
  localStorage.setItem('assistant_custom_name', name);
};

const getUserName = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  if (ecosystemUser) {
    try { 
      const parsed = JSON.parse(ecosystemUser);
      return parsed.first_name || parsed.name?.split(' ')[0] || 'toi';
    } catch(e) {}
  }
  return 'toi';
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function AssistantFullPage({ userContext = 'pro', onClose }) {
  const navigate = useNavigate();
  const userId = getUserId();
  const userName = getUserName();
  
  // Déterminer si c'est un particulier ou un pro
  const isParticulier = userContext === 'particulier';
  
  // État principal
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showArtifacts, setShowArtifacts] = useState(true);
  const [artifacts, setArtifacts] = useState([]);
  const [activeArtifact, setActiveArtifact] = useState(null);
  
  // État du nom personnalisé de l'assistant
  const [assistantName, setAssistantNameState] = useState(getAssistantName());
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [showVoiceNotes, setShowVoiceNotes] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Générer un session ID unique
  const generateSessionId = () => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGER LES SESSIONS
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const loadSessions = async () => {
      if (!userId) return;
      
      try {
        const response = await fetch(`${API_URL}/api/ai/sessions/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setSessions(data.sessions || []);
        }
      } catch (err) {
        console.error('Error loading sessions:', err);
      }
    };
    
    loadSessions();
  }, [userId]);
  
  // Nouvelle conversation
  const startNewConversation = () => {
    const newSessionId = generateSessionId();
    setCurrentSessionId(newSessionId);
    setMessages([]);
    setArtifacts([]);
    setActiveArtifact(null);
  };
  
  // Charger une session existante
  const loadSession = async (sessionId) => {
    setCurrentSessionId(sessionId);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ai/session/${sessionId}/history`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error loading session:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Auto-scroll vers le bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // ENVOYER UN MESSAGE
  // ═══════════════════════════════════════════════════════════════════════════
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const text = inputValue.trim();
    const sessionId = currentSessionId || generateSessionId();
    
    if (!currentSessionId) {
      setCurrentSessionId(sessionId);
    }
    
    // Ajouter le message utilisateur
    const userMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: sessionId,
          user_id: userId,
          assistant_name: assistantName
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const assistantMessage = { 
          role: 'assistant', 
          content: data.message, 
          timestamp: new Date().toISOString(),
          actions: data.actions
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Gérer les actions/artifacts
        if (data.actions?.length > 0) {
          handleActions(data.actions);
        }
      } else if (response.status === 402) {
        // Crédits insuffisants
        const creditError = data.detail || {};
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `💳 **Crédits insuffisants**\n\nTu as besoin de ${creditError.required || 1} crédit(s), mais il t'en reste ${creditError.available || 0}.\n\nClique sur le badge 💰 en haut pour recharger.`,
          timestamp: new Date().toISOString(),
          isError: true
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Désolé, une erreur est survenue. Réessaie !',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Oups, je n\'arrive pas à me connecter. Vérifie ta connexion.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Gérer les actions et créer des artifacts
  const handleActions = (actions) => {
    actions.forEach(action => {
      if (action.type === 'show_devis' || action.type === 'devis_created') {
        const newArtifact = {
          id: `artifact-${Date.now()}`,
          type: 'devis',
          title: 'Devis',
          icon: Receipt,
          data: action.data,
          createdAt: new Date().toISOString()
        };
        setArtifacts(prev => [...prev, newArtifact]);
        setActiveArtifact(newArtifact);
        setShowArtifacts(true);
      } else if (action.type === 'show_tasks' || action.type === 'task_created') {
        const newArtifact = {
          id: `artifact-${Date.now()}`,
          type: 'tasks',
          title: 'Tâches',
          icon: ListTodo,
          data: action.data,
          createdAt: new Date().toISOString()
        };
        setArtifacts(prev => [...prev, newArtifact]);
        setActiveArtifact(newArtifact);
        setShowArtifacts(true);
      } else if (action.type === 'navigate') {
        // Navigation - on peut l'afficher comme suggestion
      }
    });
  };
  
  // Gérer Enter pour envoyer
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="h-screen flex bg-gray-50 overflow-hidden">
      {/* ══════════════════════════════════════════════════════════════════════
          SIDEBAR - Historique & Projets
      ══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showSidebar && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full bg-[#1E3A5F] text-white flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  {isEditingName ? (
                    <div className="flex items-center gap-1 flex-1">
                      <input
                        type="text"
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const newName = tempName.trim() || 'Mon Assistant';
                            setAssistantName(newName);
                            setAssistantNameState(newName);
                            setIsEditingName(false);
                          } else if (e.key === 'Escape') {
                            setIsEditingName(false);
                          }
                        }}
                        className="flex-1 bg-white/10 text-white text-sm px-2 py-1 rounded border border-white/20 focus:outline-none focus:border-amber-400"
                        autoFocus
                        placeholder="Nomme-moi..."
                      />
                      <button
                        onClick={() => {
                          const newName = tempName.trim() || 'Mon Assistant';
                          setAssistantName(newName);
                          setAssistantNameState(newName);
                          setIsEditingName(false);
                        }}
                        className="p-1 hover:bg-white/10 rounded"
                      >
                        <Check className="w-4 h-4 text-emerald-400" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="font-semibold truncate">{assistantName}</span>
                      <button
                        onClick={() => {
                          setTempName(assistantName);
                          setIsEditingName(true);
                        }}
                        className="p-1 hover:bg-white/10 rounded opacity-50 hover:opacity-100 transition-opacity"
                        title="Renommer ton assistant"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
                <button
                  onClick={() => onClose ? onClose() : navigate(-1)}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                  title="Retour"
                >
                  <Home className="w-4 h-4" />
                </button>
              </div>
              
              <button
                onClick={startNewConversation}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Nouvelle conversation
              </button>
            </div>
            
            {/* Liste des conversations */}
            <div className="flex-1 overflow-y-auto p-2">
              {/* Aujourd'hui */}
              <div className="mb-4">
                <div className="flex items-center gap-2 px-2 py-1 text-xs text-white/50 uppercase tracking-wider">
                  <Clock className="w-3 h-3" />
                  Aujourd'hui
                </div>
                
                {sessions.slice(0, 5).map((session) => (
                  <button
                    key={session.session_id}
                    onClick={() => loadSession(session.session_id)}
                    className={`
                      w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm
                      transition-colors group
                      ${currentSessionId === session.session_id 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    <MessageSquare className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate flex-1">
                      {session.title || session.last_message?.slice(0, 30) || 'Conversation'}
                    </span>
                  </button>
                ))}
                
                {sessions.length === 0 && (
                  <p className="px-3 py-2 text-white/40 text-sm">
                    Aucune conversation
                  </p>
                )}
              </div>
              
              {/* Projets (futurs) */}
              <div className="mb-4">
                <div className="flex items-center gap-2 px-2 py-1 text-xs text-white/50 uppercase tracking-wider">
                  <FolderOpen className="w-3 h-3" />
                  Projets
                </div>
                <p className="px-3 py-2 text-white/40 text-sm italic">
                  Bientôt disponible
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-3 border-t border-white/10">
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/5 text-sm">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-semibold text-xs">
                  {userName[0]?.toUpperCase()}
                </div>
                <span className="text-white/80 truncate">{userName}</span>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
      
      {/* ══════════════════════════════════════════════════════════════════════
          ZONE PRINCIPALE - Chat
      ══════════════════════════════════════════════════════════════════════ */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-12 px-4 flex items-center justify-between bg-white border-b border-gray-200">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
            >
              <MessageSquare className="w-4 h-4 text-gray-600" />
            </button>
            <h1 className="text-sm font-medium text-gray-600">
              {currentSessionId ? 'Conversation' : 'Nouvelle conversation'}
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowArtifacts(!showArtifacts)}
              className={`
                p-2 rounded-lg transition-colors
                ${showArtifacts ? 'bg-[#1E3A5F] text-white' : 'hover:bg-gray-100 text-gray-600'}
              `}
              title={showArtifacts ? 'Masquer le panneau' : 'Afficher le panneau'}
            >
              {showArtifacts ? <PanelRightClose className="w-5 h-5" /> : <PanelRightOpen className="w-5 h-5" />}
            </button>
          </div>
        </header>
        
        {/* Zone de messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-6">
            {messages.length === 0 ? (
              // État vide - Suggestions
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  Bonjour {userName ? userName : 'toi'} !
                </h2>
                <p className="text-gray-500 mb-2">
                  Je suis ton super assistant. Tu peux m'appeler comme tu le souhaites.
                </p>
                <button
                  onClick={() => {
                    setTempName(assistantName);
                    setIsEditingName(true);
                    setShowSidebar(true);
                  }}
                  className="text-sm text-[#1E3A5F] hover:underline mb-6 inline-flex items-center gap-1"
                >
                  <Pencil className="w-3 h-3" />
                  Me renommer
                </button>
                <p className="text-gray-500 mb-8">
                  Comment puis-je t'aider aujourd'hui ?
                </p>
                
                {/* Suggestions rapides - Différentes selon Pro ou Particulier */}
                <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
                  {(isParticulier ? [
                    { icon: Search, text: 'Trouver un artisan', action: 'Je cherche un artisan près de chez moi' },
                    { icon: FileText, text: 'Créer un projet', action: 'Aide-moi à créer un projet de travaux' },
                    { icon: ListTodo, text: 'Mes projets', action: 'Où en sont mes projets en cours ?' },
                    { icon: Calendar, text: 'Mes rendez-vous', action: 'Quels sont mes prochains rendez-vous ?' },
                  ] : [
                    { icon: Receipt, text: 'Créer un devis', action: 'Aide-moi à créer un devis' },
                    { icon: ListTodo, text: 'Voir mes tâches', action: 'Quelles sont mes tâches du jour ?' },
                    { icon: Calendar, text: 'Mon agenda', action: 'Qu\'est-ce que j\'ai prévu cette semaine ?' },
                    { icon: Users, text: 'Mes messages', action: 'Ai-je des messages non lus ?' },
                  ]).map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInputValue(item.action);
                        inputRef.current?.focus();
                      }}
                      className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-[#1E3A5F] hover:shadow-sm transition-all text-left"
                    >
                      <item.icon className="w-5 h-5 text-[#1E3A5F]" />
                      <span className="text-sm text-gray-700">{item.text}</span>
                    </button>
                  ))}
                </div>
                
                {/* Bouton démo pour tester les artifacts */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-3">Tester l'affichage des résultats :</p>
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => {
                        const demoDevis = {
                          id: 'DEV-2026-001',
                          number: 'DEV-2026-001',
                          title: 'Rénovation salle de bain',
                          status: 'draft',
                          client: {
                            name: 'Marie Dupont',
                            email: 'marie.dupont@email.com',
                            phone: '06 12 34 56 78',
                            address: '15 rue des Lilas',
                            postal_code: '75011',
                            city: 'Paris'
                          },
                          lines: [
                            { id: '1', description: 'Dépose carrelage existant', quantity: 12, unit: 'm²', unit_price: 25 },
                            { id: '2', description: 'Pose nouveau carrelage', quantity: 12, unit: 'm²', unit_price: 65 },
                            { id: '3', description: 'Installation douche italienne', quantity: 1, unit: 'forfait', unit_price: 1200 },
                          ],
                          vat_rate: 10,
                          discount_percent: 0
                        };
                        const newArtifact = {
                          id: `artifact-devis-${Date.now()}`,
                          type: 'devis',
                          title: 'Devis Rénovation SDB',
                          icon: Receipt,
                          data: demoDevis,
                          createdAt: new Date().toISOString()
                        };
                        setArtifacts(prev => [...prev, newArtifact]);
                        setActiveArtifact(newArtifact);
                        setShowArtifacts(true);
                      }}
                      className="px-3 py-1.5 text-xs bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8a] transition-colors"
                    >
                      📄 Démo Devis
                    </button>
                    <button
                      onClick={() => {
                        const demoTasks = [
                          { id: '1', title: 'Rappeler client M. Martin', priority: 'high', due_date: new Date().toISOString() },
                          { id: '2', title: 'Envoyer devis rénovation', priority: 'normal', completed: false },
                          { id: '3', title: 'Commander matériaux chantier Dupont', priority: 'normal' },
                          { id: '4', title: 'Vérifier planning semaine prochaine', priority: 'low', completed: true }
                        ];
                        const newArtifact = {
                          id: `artifact-tasks-${Date.now()}`,
                          type: 'tasks',
                          title: 'Mes Tâches',
                          icon: ListTodo,
                          data: demoTasks,
                          createdAt: new Date().toISOString()
                        };
                        setArtifacts(prev => [...prev, newArtifact]);
                        setActiveArtifact(newArtifact);
                        setShowArtifacts(true);
                      }}
                      className="px-3 py-1.5 text-xs bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                    >
                      ✅ Démo Tâches
                    </button>
                    {/* Bouton Démo Projet - uniquement pour particuliers */}
                    {isParticulier && (
                      <button
                        onClick={() => {
                          const newArtifact = {
                            id: `artifact-project-${Date.now()}`,
                            type: 'project',
                            title: DEMO_PROJECT.title,
                            icon: FolderOpen,
                            data: DEMO_PROJECT,
                            createdAt: new Date().toISOString()
                          };
                          setArtifacts(prev => [...prev, newArtifact]);
                          setActiveArtifact(newArtifact);
                          setShowArtifacts(true);
                        }}
                        className="px-3 py-1.5 text-xs bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                      >
                        📁 Démo Projet
                      </button>
                    )}
                  </div>
                  
                  {/* Boutons Démo Recherche - Moteur de recherche écosystème */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-400 mb-3">Rechercher dans l'écosystème :</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <button
                        onClick={() => {
                          const newArtifact = {
                            id: `artifact-search-artisans-${Date.now()}`,
                            type: 'search_artisans',
                            title: 'Artisans',
                            icon: Users,
                            data: { initialQuery: '' },
                            createdAt: new Date().toISOString()
                          };
                          setArtifacts(prev => [...prev, newArtifact]);
                          setActiveArtifact(newArtifact);
                          setShowArtifacts(true);
                        }}
                        className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                      >
                        <Users className="w-3.5 h-3.5" />
                        Artisans
                      </button>
                      <button
                        onClick={() => {
                          const newArtifact = {
                            id: `artifact-search-produits-${Date.now()}`,
                            type: 'search_produits',
                            title: 'Produits',
                            icon: Package,
                            data: { initialQuery: '' },
                            createdAt: new Date().toISOString()
                          };
                          setArtifacts(prev => [...prev, newArtifact]);
                          setActiveArtifact(newArtifact);
                          setShowArtifacts(true);
                        }}
                        className="px-3 py-1.5 text-xs bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-1"
                      >
                        <Package className="w-3.5 h-3.5" />
                        Produits
                      </button>
                      <button
                        onClick={() => {
                          const newArtifact = {
                            id: `artifact-search-posts-${Date.now()}`,
                            type: 'search_posts',
                            title: 'Publications',
                            icon: MessageSquare,
                            data: { initialQuery: '' },
                            createdAt: new Date().toISOString()
                          };
                          setArtifacts(prev => [...prev, newArtifact]);
                          setActiveArtifact(newArtifact);
                          setShowArtifacts(true);
                        }}
                        className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Publications
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Messages
              <div className="space-y-6">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                    )}
                    
                    <div
                      className={`
                        max-w-[80%] rounded-2xl px-4 py-3
                        ${msg.role === 'user' 
                          ? 'bg-[#1E3A5F] text-white rounded-br-md' 
                          : msg.isError
                            ? 'bg-red-50 text-red-800 border border-red-200'
                            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                        }
                      `}
                    >
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-sm max-w-none">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                    
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm">
                        {userName[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>
        
        {/* Zone de saisie */}
        <div className="p-4 bg-white border-t border-gray-200">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-2xl p-2 focus-within:border-[#1E3A5F] focus-within:ring-2 focus-within:ring-[#1E3A5F]/10 transition-all">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Écris ton message..."
                rows={1}
                className="flex-1 bg-transparent border-none outline-none resize-none px-2 py-1.5 text-gray-800 placeholder-gray-400 max-h-32"
                style={{ minHeight: '24px' }}
              />
              
              {/* Bouton Notes Vocales */}
              <button
                onClick={() => setShowVoiceNotes(true)}
                className="p-2.5 rounded-xl transition-all bg-gray-100 hover:bg-[#F97316]/10 text-gray-500 hover:text-[#F97316] group relative"
                title="Notes vocales"
                data-testid="chat-voice-notes-btn"
              >
                <Mic className="w-5 h-5" />
                <Sparkles className="w-2.5 h-2.5 absolute -top-0.5 -right-0.5 text-[#F97316]" />
              </button>
              
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className={`
                  p-2.5 rounded-xl transition-all
                  ${inputValue.trim() && !isLoading
                    ? 'bg-[#1E3A5F] text-white hover:bg-[#2d5a8a]'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            
            <p className="text-center text-xs text-gray-400 mt-2">
              Ton assistant peut faire des erreurs. Vérifie les informations importantes.
            </p>
          </div>
        </div>
      </main>
      
      {/* ══════════════════════════════════════════════════════════════════════
          PANNEAU ARTIFACTS - Résultats, Devis, Tâches, etc.
      ══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showArtifacts && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 550, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full bg-white border-l border-gray-200 flex flex-col overflow-hidden"
          >
            {/* Header Artifacts avec Actions Globales */}
            <div className="h-12 px-3 flex items-center justify-between border-b border-gray-200 bg-white">
              <h2 className="text-sm font-medium text-gray-600">Résultats</h2>
              
              {/* Actions globales (visibles quand un artifact est actif) */}
              <div className="flex items-center gap-0.5">
                {activeArtifact && (
                  <>
                    <button
                      onClick={() => {
                        // Déclencher le mode édition dans le composant enfant
                        const event = new CustomEvent('artifact-edit', { detail: activeArtifact.id });
                        window.dispatchEvent(event);
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                      title="Modifier"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        const content = JSON.stringify(activeArtifact.data, null, 2);
                        navigator.clipboard.writeText(content);
                        toast.success('Copié dans le presse-papier');
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                      title="Copier"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        const event = new CustomEvent('artifact-download', { detail: activeArtifact });
                        window.dispatchEvent(event);
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                      title="Télécharger PDF"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <div className="w-px h-4 bg-gray-200 mx-1" />
                    <button
                      onClick={() => {
                        const event = new CustomEvent('artifact-send-email', { detail: activeArtifact });
                        window.dispatchEvent(event);
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                      title="Envoyer par mail"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        const event = new CustomEvent('artifact-send-message', { detail: activeArtifact });
                        window.dispatchEvent(event);
                        toast.info('Envoyer par message - Sélectionne un contact');
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                      title="Envoyer par message"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                    <div className="w-px h-4 bg-gray-200 mx-1" />
                  </>
                )}
                <button
                  onClick={() => setShowArtifacts(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
            
            {/* Liste des artifacts */}
            {artifacts.length > 0 ? (
              <div className="flex-1 overflow-y-auto">
                {/* Tabs des artifacts */}
                <div className="flex gap-1 p-2 border-b border-gray-100 overflow-x-auto">
                  {artifacts.map((artifact) => {
                    const Icon = artifact.icon || FileText;
                    const isActive = activeArtifact?.id === artifact.id;
                    
                    return (
                      <button
                        key={artifact.id}
                        onClick={() => setActiveArtifact(artifact)}
                        className={`
                          flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap
                          transition-colors
                          ${isActive 
                            ? 'bg-[#1E3A5F] text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }
                        `}
                      >
                        <Icon className="w-4 h-4" />
                        {artifact.title}
                      </button>
                    );
                  })}
                </div>
                
                {/* Contenu de l'artifact actif */}
                <div className="flex-1 overflow-hidden">
                  {activeArtifact?.type === 'devis' && (
                    <ArtifactDevisInteractif 
                      data={activeArtifact.data} 
                      onUpdate={(updatedDevis) => {
                        // Mettre à jour l'artifact
                        setArtifacts(prev => prev.map(a => 
                          a.id === activeArtifact.id ? { ...a, data: updatedDevis } : a
                        ));
                      }}
                    />
                  )}
                  {activeArtifact?.type === 'tasks' && (
                    <ArtifactTasksInteractif 
                      data={activeArtifact.data}
                      onUpdate={(updatedTasks) => {
                        setArtifacts(prev => prev.map(a => 
                          a.id === activeArtifact.id ? { ...a, data: updatedTasks } : a
                        ));
                      }}
                    />
                  )}
                  {activeArtifact?.type === 'project' && (
                    <ArtifactProjetInteractif 
                      project={activeArtifact.data}
                      onUpdate={(updatedProject) => {
                        setArtifacts(prev => prev.map(a => 
                          a.id === activeArtifact.id ? { ...a, data: updatedProject } : a
                        ));
                      }}
                    />
                  )}
                  {activeArtifact?.type === 'search_artisans' && (
                    <ArtifactSearchArtisans 
                      initialQuery={activeArtifact.data?.initialQuery || ''}
                    />
                  )}
                  {activeArtifact?.type === 'search_produits' && (
                    <ArtifactSearchProduits 
                      initialQuery={activeArtifact.data?.initialQuery || ''}
                    />
                  )}
                  {activeArtifact?.type === 'search_posts' && (
                    <ArtifactSearchPosts 
                      initialQuery={activeArtifact.data?.initialQuery || ''}
                    />
                  )}
                </div>
              </div>
            ) : (
              // État vide
              <div className="flex-1 flex items-center justify-center p-8 text-center">
                <div>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm">
                    Les résultats de tes demandes apparaîtront ici
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Devis, tâches, documents...
                  </p>
                </div>
              </div>
            )}
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Modal Notes Vocales - Superpouvoir */}
      <AnimatePresence>
        {showVoiceNotes && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowVoiceNotes(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <VoiceNotesSuperpouvoir 
                userId={getUserId()}
                onClose={() => setShowVoiceNotes(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
